<template>
    <!-- No live region: the store announces the start and the outcome, which this text would repeat. -->
    <div
        v-if="run"
        ref="status"
        tabindex="-1"
        class="flex w-full flex-col gap-2 rounded-xl border border-theme-border bg-theme-bg px-4 py-3 text-xs text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
        dusk="AddOnsBatchStatus"
    >
        <template v-if="addOnBatchRunning">
            <p :id="ids.progress">{{ progressText }}</p>
            <div
                role="progressbar"
                :aria-labelledby="ids.progress"
                aria-valuemin="0"
                :aria-valuemax="run.total"
                :aria-valuenow="done || null"
                class="h-1.5 w-full overflow-hidden rounded-full bg-theme-progress-track"
            >
                <!-- A request reports nothing until it answers, and a batch of up to a hundred is one request:
                     until the first answer the bar sweeps, as the toast's wait bar does. -->
                <div
                    v-if="done === 0"
                    class="h-full w-1/3 animate-toast-progress rounded-full bg-theme-accent motion-reduce:w-full motion-reduce:animate-none"
                />
                <div v-else class="h-full rounded-full bg-theme-accent" :style="{ width: `${percent}%` }" />
            </div>
        </template>

        <template v-else>
            <div class="flex items-center justify-between gap-2">
                <p class="text-sm font-semibold">{{ outcomeText }}</p>
                <PillIconButton
                    icon="close"
                    variant="muted"
                    touch-target
                    :icon-size="18"
                    dusk="AddOnsBatchDismiss"
                    :aria-label="strings.batchDismiss"
                    :title="strings.batchDismiss"
                    class="-my-1.5 -mr-2 shrink-0"
                    @click="dismiss"
                />
            </div>
            <p v-if="run.imagesFollow" dusk="AddOnsBatchImagesFollow">{{ strings.batchImagesFollow }}</p>
            <!-- A failure the host did not refuse is not the sheet's fault, so the same sheet adds it on a second run. -->
            <p v-if="run.failures.some(failure => !failure.message)" dusk="AddOnsBatchRetryMissing">
                {{ strings.batchRetryMissing }}
            </p>
            <details v-if="problems" dusk="AddOnsBatchFailures">
                <summary :class="classes.summary">{{ strings.batchFailuresToggle }}</summary>
                <ul class="mt-1 max-h-40 list-disc space-y-1 overflow-auto pl-5 text-theme-danger">
                    <li v-for="(failure, index) in run.failures" :key="`failure-${index}`">
                        {{ failureText(failure) }}
                    </li>
                    <li v-for="(issue, index) in run.imageIssues" :key="`image-${index}`">
                        {{ imageIssueText(issue) }}
                    </li>
                </ul>
            </details>
        </template>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import PillIconButton from '../Components/PillIconButton.vue';
import { addOnStrings, fill } from './addOnStrings.js';

const CLASSES = {
    summary:
        'min-h-11 md:min-h-0 cursor-pointer rounded-md py-1 font-medium text-theme-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
};

export default {
    components: { PillIconButton },
    emits: ['dismissed'],
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters({ run: 'addOnBatchRun', addOnBatchRunning: 'addOnBatchRunning' }),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        classes() {
            return CLASSES;
        },
        ids() {
            return { progress: `add-on-batch-status-${this._uid}` };
        },
        done() {
            return this.run.created + this.run.failures.length;
        },
        percent() {
            return this.run.total ? Math.round((this.done / this.run.total) * 100) : 0;
        },
        progressText() {
            return fill(this.strings.batchProgressCreating, { done: this.done, total: this.run.total });
        },
        /** Rows not added, and added rows whose image did not load here. */
        problems() {
            return this.run.failures.length + this.run.imageIssues.length;
        },
        outcomeText() {
            const { created } = this.run;
            if (this.problems === 0) return fill(this.strings.batchDone, { created });

            return fill(this.strings.batchDoneWithFailures, { created, failed: this.problems });
        },
    },
    watch: {
        // The import button is disabled while its batch runs, so the focus the dialog hands back goes to the progress.
        addOnBatchRunning(running) {
            if (running) this.$nextTick(() => this.$refs.status?.focus());
        },
    },
    methods: {
        ...mapActions(['dismissAddOnBatch']),
        failureText({ line, title, message }) {
            return fill(this.strings.batchFailureCreate, {
                line,
                title,
                reason: message ?? this.strings.batchFailureNoReason,
            });
        },
        imageIssueText({ line, title }) {
            return fill(this.strings.batchImageIssue, { line, title });
        },
        /** The button that held focus goes with the outcome, so the panel gives it back somewhere that stays. */
        dismiss() {
            this.dismissAddOnBatch();
            this.$emit('dismissed');
        },
    },
};
</script>
